import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { User, Mail, Check, AlertCircle, ChevronDown, ChevronUp, Package, Folder, FolderOpen, Key, Download, Copy, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ticketService } from '../../services/api';

/**
 * AccountsView - Vue par comptes email avec groupement par SOURCE
 * Niveau 1: SOURCE (TOTEM, BROCK, etc.)
 * Niveau 2: Emails des comptes
 * Niveau 3: Matchs et catégories
 */
const AccountsView = ({ tickets, onAccountClick, selectedAccount, onRefresh }) => {
  const [sortBy, setSortBy] = useState('reste'); // 'reste', 'total', 'email'
  const [showOnlyReady, setShowOnlyReady] = useState(false);
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set()); // État des catégories ouvertes
  const [viewMode, setViewMode] = useState('source'); // 'source' ou 'flat'
  
  // État pour la validation
  const [validationModal, setValidationModal] = useState(null); // { refVente, nomClient, mailClient, qty, accountEmail, accountPassword, matchNum, category }
  const [isValidating, setIsValidating] = useState(false);

  // Toggle category expansion
  const toggleCategoryExpand = (categoryKey) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Fonction pour valider une vente
  const handleValidateVente = async () => {
    if (!validationModal) return;
    
    setIsValidating(true);
    try {
      const result = await ticketService.bulkValidate([validationModal.refVente]);
      
      if (result.validated_count > 0) {
        toast.success(`✅ Vente ${validationModal.refVente} validée !`);
        setValidationModal(null);
        // Rafraîchir les données
        if (onRefresh) onRefresh();
      } else if (result.already_validated?.includes(validationModal.refVente)) {
        toast.info('Cette vente est déjà validée.');
        setValidationModal(null);
      } else {
        toast.error('Erreur lors de la validation.');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  // Calculer les stats par SOURCE puis par EMAIL
  const { sourceStats, flatAccountStats } = useMemo(() => {
    if (!tickets || tickets.length === 0) return { sourceStats: [], flatAccountStats: [] };

    // Ne prendre que les ACHAT non annulés et non validés
    // Les ACHAT validés sont considérés comme terminés et ne doivent plus apparaître
    const achatTickets = tickets.filter(t => 
      t.type === 'ACHAT' && 
      t.statut !== 'ANNULEE' && 
      t.statut !== 'ANNULÉ' &&
      t.statut !== 'VALIDE' &&
      t.statut !== 'VALIDÉ'
    );

    const sourceMap = {};
    const flatStats = {};

    achatTickets.forEach(ticket => {
      const email = ticket.email_compte;
      if (!email || email === 'N/A' || email === '-' || email === '') return;

      const source = ticket.source || 'AUTRE';
      const qty = parseInt(ticket.qty) || 1;
      const hasRefVente = ticket.ref_vente && ticket.ref_vente !== '' && ticket.ref_vente !== '-' && ticket.ref_vente !== 'N/A';
      const isAttribue = ticket.attribue === true || hasRefVente || ticket.statut === 'MATCH_OK' || ticket.statut === 'SPECIAL_MATCH';

      // Stats par SOURCE
      if (!sourceMap[source]) {
        sourceMap[source] = {
          source,
          totalQty: 0,
          totalAttribue: 0,
          totalReste: 0,
          accounts: {},
          accountCount: 0
        };
      }

      // Stats par EMAIL dans cette source
      if (!sourceMap[source].accounts[email]) {
        sourceMap[source].accounts[email] = {
          email,
          source,
          password: ticket.pw_compte || '****',
          totalQty: 0,
          totalAttribue: 0,
          totalReste: 0,
          matches: {},
          ticketIds: []
        };
      }

      // Mise à jour des stats
      sourceMap[source].totalQty += qty;
      sourceMap[source].accounts[email].totalQty += qty;
      sourceMap[source].accounts[email].ticketIds.push(ticket.id);

      if (isAttribue) {
        sourceMap[source].totalAttribue += qty;
        sourceMap[source].accounts[email].totalAttribue += qty;
      } else {
        sourceMap[source].totalReste += qty;
        sourceMap[source].accounts[email].totalReste += qty;
      }

      // Stats par match
      const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
      const matchKey = matchNumMatch ? `M${matchNumMatch[1]}` : 'Autre';
      const cat = ticket.categorie || 'N/A';

      if (!sourceMap[source].accounts[email].matches[matchKey]) {
        sourceMap[source].accounts[email].matches[matchKey] = {
          matchNum: matchKey,
          eventName: ticket.evenement,
          totalQty: 0,
          attribue: 0,
          reste: 0,
          categories: {}
        };
      }

      sourceMap[source].accounts[email].matches[matchKey].totalQty += qty;
      if (isAttribue) {
        sourceMap[source].accounts[email].matches[matchKey].attribue += qty;
      } else {
        sourceMap[source].accounts[email].matches[matchKey].reste += qty;
      }

      // Par catégorie - stocker aussi les infos de vente
      if (!sourceMap[source].accounts[email].matches[matchKey].categories[cat]) {
        sourceMap[source].accounts[email].matches[matchKey].categories[cat] = { 
          qty: 0, 
          attribue: 0,
          venteDetails: [] // Stocker les détails des ventes liées
        };
      }
      sourceMap[source].accounts[email].matches[matchKey].categories[cat].qty += qty;
      if (isAttribue) {
        sourceMap[source].accounts[email].matches[matchKey].categories[cat].attribue += qty;
        // Ajouter les détails de la vente
        if (ticket.ref_vente) {
          sourceMap[source].accounts[email].matches[matchKey].categories[cat].venteDetails.push({
            refVente: ticket.ref_vente,
            nomClient: ticket.nom_client || '-',
            mailClient: ticket.mail_client || '-',
            qty: qty
          });
        }
      }

      // Flat stats (pour vue sans groupement)
      if (!flatStats[email]) {
        flatStats[email] = sourceMap[source].accounts[email];
      }
    });

    // Convertir et compter les comptes par source
    const sourceList = Object.values(sourceMap).map(s => {
      const accountList = Object.values(s.accounts);
      s.accountCount = accountList.length;
      s.readyCount = accountList.filter(a => a.totalReste === 0 && a.totalQty > 0).length;
      
      // Trier les comptes dans chaque source
      if (sortBy === 'reste') {
        accountList.sort((a, b) => a.totalReste - b.totalReste);
      } else if (sortBy === 'total') {
        accountList.sort((a, b) => b.totalQty - a.totalQty);
      } else {
        accountList.sort((a, b) => a.email.localeCompare(b.email));
      }
      s.accountList = accountList;
      return s;
    });

    // Trier les sources par reste
    sourceList.sort((a, b) => a.totalReste - b.totalReste);

    // Flat list
    let flatList = Object.values(flatStats);
    if (showOnlyReady) {
      flatList = flatList.filter(a => a.totalReste === 0 && a.totalQty > 0);
    }
    if (sortBy === 'reste') {
      flatList.sort((a, b) => a.totalReste - b.totalReste);
    } else if (sortBy === 'total') {
      flatList.sort((a, b) => b.totalQty - a.totalQty);
    } else {
      flatList.sort((a, b) => a.email.localeCompare(b.email));
    }

    return { sourceStats: sourceList, flatAccountStats: flatList };
  }, [tickets, sortBy, showOnlyReady]);

  // Stats globales
  const globalStats = useMemo(() => {
    const total = flatAccountStats.length;
    const ready = flatAccountStats.filter(a => a.totalReste === 0 && a.totalQty > 0).length;
    const partial = flatAccountStats.filter(a => a.totalReste > 0 && a.totalAttribue > 0).length;
    const totalSources = sourceStats.length;
    return { total, ready, partial, totalSources };
  }, [flatAccountStats, sourceStats]);

  const toggleSourceExpand = (source) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(source)) {
      newExpanded.delete(source);
    } else {
      newExpanded.add(source);
    }
    setExpandedSources(newExpanded);
  };

  const toggleAccountExpand = (email) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(email)) {
      newExpanded.delete(email);
    } else {
      newExpanded.add(email);
    }
    setExpandedAccounts(newExpanded);
  };

  // Rendu d'un compte
  const renderAccount = (account, inSourceView = false) => {
    const isReady = account.totalReste === 0 && account.totalQty > 0;
    const isSelected = selectedAccount === account.email;
    const isExpanded = expandedAccounts.has(account.email);
    const matchList = Object.values(account.matches).sort((a, b) => {
      const numA = parseInt(a.matchNum.replace('M', '')) || 999;
      const numB = parseInt(b.matchNum.replace('M', '')) || 999;
      return numA - numB;
    });

    return (
      <div
        key={account.email}
        className={cn(
          "bg-zinc-900/80 border rounded-lg overflow-hidden transition-all",
          isSelected ? "border-[#C4A052] ring-2 ring-[#C4A052]/30" : "border-zinc-700",
          isReady ? "border-l-4 border-l-green-500" : account.totalAttribue > 0 ? "border-l-4 border-l-orange-500" : ""
        )}
      >
        {/* Header du compte */}
        <div
          onClick={() => onAccountClick?.(account.email)}
          className="p-3 cursor-pointer hover:bg-zinc-800/50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Mail className="h-4 w-4 text-[#C4A052] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                {/* Email du compte avec bouton copier */}
                <div className="flex items-center gap-1">
                  <span className="text-white font-medium truncate text-sm" title={account.email}>
                    {account.email}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(account.email || '');
                      toast.success('Email copié !');
                    }}
                    className="p-0.5 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                    title="Copier l'email"
                  >
                    <Copy className="h-3 w-3 text-zinc-500 hover:text-white" />
                  </button>
                </div>
                {/* Mot de passe du compte */}
                <div className="flex items-center gap-1 mt-0.5">
                  <Key className="h-3 w-3 text-zinc-500" />
                  <span className="text-xs text-zinc-400 font-mono">{account.password || '****'}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(account.password || '');
                      toast.success('Mot de passe copié !');
                    }}
                    className="p-0.5 hover:bg-zinc-700 rounded transition-colors"
                    title="Copier le mot de passe"
                  >
                    <Copy className="h-3 w-3 text-zinc-500 hover:text-white" />
                  </button>
                </div>
              </div>
            </div>
            <div className={cn(
              "px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ml-2",
              isReady ? "bg-green-500/20 text-green-400" : "bg-orange-500/20 text-orange-400"
            )}>
              {isReady ? "PRÊT" : `RESTE ${account.totalReste}`}
            </div>
          </div>

          <div className="flex items-center justify-between mt-2 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-zinc-400">
                <Package className="h-3 w-3 inline mr-1" />
                {account.totalQty} billets
              </span>
              <span className="text-green-400">{account.totalAttribue} attrib.</span>
            </div>
            <span className="text-zinc-500">{matchList.length} match(s)</span>
          </div>
        </div>

        {/* Bouton expand */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleAccountExpand(account.email); }}
          className="w-full px-3 py-1.5 bg-zinc-800/50 border-t border-zinc-700 flex items-center justify-center gap-1 text-xs text-zinc-400 hover:text-white hover:bg-zinc-700/50 transition-colors"
        >
          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {isExpanded ? "Masquer" : "Voir les matchs"}
        </button>

        {/* Détails des matchs */}
        {isExpanded && (
          <div className="p-3 border-t border-zinc-700 bg-zinc-950/50 space-y-2">
            {matchList.map(match => (
              <div key={match.matchNum} className="bg-zinc-800/50 rounded p-2">
                <div className="flex items-center justify-between">
                  <span className="bg-[#C4A052] text-black text-xs font-bold px-2 py-0.5 rounded">
                    {match.matchNum}
                  </span>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-white">{match.totalQty}</span>
                    <span className="text-green-400">{match.attribue}</span>
                    <span className="text-zinc-500">/</span>
                    {match.reste > 0 ? (
                      <span className="text-orange-400">R:{match.reste}</span>
                    ) : (
                      <span className="text-green-400">✓</span>
                    )}
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {Object.entries(match.categories).map(([cat, data]) => {
                    const categoryKey = `${account.email}-${match.matchNum}-${cat}`;
                    return (
                      <CategoryDropdown 
                        key={cat} 
                        cat={cat} 
                        data={data}
                        categoryKey={categoryKey}
                        isOpen={expandedCategories.has(categoryKey)}
                        onToggle={() => toggleCategoryExpand(categoryKey)}
                        accountEmail={account.email}
                        accountPassword={account.password}
                        matchNum={match.matchNum}
                        onValidate={(venteInfo) => setValidationModal({
                          ...venteInfo,
                          accountEmail: account.email,
                          accountPassword: account.password,
                          matchNum: match.matchNum,
                          category: cat
                        })}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Composant pour afficher une catégorie avec dropdown des détails de vente
  const CategoryDropdown = ({ cat, data, categoryKey, isOpen, onToggle, accountEmail, accountPassword, matchNum, onValidate }) => {
    const hasVenteDetails = data.venteDetails && data.venteDetails.length > 0;

    // Grouper les ventes par ref_vente pour éviter les doublons
    const groupedVentes = useMemo(() => {
      if (!hasVenteDetails) return [];
      
      const grouped = {};
      data.venteDetails.forEach(vente => {
        const key = vente.refVente;
        if (grouped[key]) {
          grouped[key].qty += vente.qty;
        } else {
          grouped[key] = { ...vente };
        }
      });
      
      return Object.values(grouped);
    }, [data.venteDetails, hasVenteDetails]);

    return (
      <div className="bg-zinc-900/50 rounded overflow-hidden">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (hasVenteDetails) onToggle();
          }}
          className={cn(
            "w-full flex items-center justify-between px-2 py-1 text-xs",
            hasVenteDetails ? "hover:bg-zinc-800 cursor-pointer" : "cursor-default"
          )}
        >
          <span className="text-zinc-300">{cat}</span>
          <div className="flex items-center gap-2">
            <span className="text-green-400">{data.attribue}</span>
            <span className="text-zinc-500">/</span>
            <span className="text-white">{data.qty}</span>
            {hasVenteDetails && (
              <ChevronDown className={cn(
                "h-3 w-3 text-zinc-400 transition-transform",
                isOpen && "rotate-180"
              )} />
            )}
          </div>
        </button>
        
        {/* Dropdown avec détails des ventes - groupées par ref */}
        {isOpen && hasVenteDetails && (
          <div 
            className="border-t border-zinc-800 bg-zinc-950/80 p-2 space-y-1"
            onClick={(e) => e.stopPropagation()}
          >
            {groupedVentes.map((vente, idx) => (
              <div key={idx} className="text-[10px] bg-zinc-900/50 rounded p-1.5 space-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-400 font-medium">REF: {vente.refVente}</span>
                    {/* Bouton Valider */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onValidate({
                          refVente: vente.refVente,
                          nomClient: vente.nomClient,
                          mailClient: vente.mailClient,
                          qty: vente.qty
                        });
                      }}
                      className="px-1.5 py-0.5 bg-green-500/20 hover:bg-green-500/40 text-green-400 rounded flex items-center gap-1 transition-colors"
                      title="Valider cette vente"
                    >
                      <CheckCircle className="h-3 w-3" />
                      <span className="text-[9px] font-medium">Valider</span>
                    </button>
                  </div>
                  <span className={cn(
                    "font-bold px-1.5 py-0.5 rounded",
                    vente.qty > 1 ? "bg-amber-500/20 text-amber-300" : "text-zinc-500"
                  )}>
                    x{vente.qty}
                  </span>
                </div>
                <div className="text-zinc-400">
                  <span className="text-zinc-500">Client:</span> {vente.nomClient}
                </div>
                <div className="flex items-center gap-1 text-zinc-400">
                  <span className="text-zinc-500">Email:</span> 
                  <span className="truncate">{vente.mailClient}</span>
                  {vente.mailClient && vente.mailClient !== '-' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(vente.mailClient);
                        toast.success('Email client copié !');
                      }}
                      className="p-0.5 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                      title="Copier l'email client"
                    >
                      <Copy className="h-2.5 w-2.5 text-zinc-500 hover:text-white" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Fonction d'export du récap
  const handleExportRecap = () => {
    const rows = [
      ['Source', 'Email Compte', 'PW Compte', 'Match', 'Catégorie', 'Total', 'Attribués', 'Reste', 'Ref Vente', 'Qty Vente', 'Nom Client', 'Mail Client']
    ];

    flatAccountStats.forEach(account => {
      Object.values(account.matches).forEach(match => {
        Object.entries(match.categories).forEach(([cat, data]) => {
          if (data.venteDetails && data.venteDetails.length > 0) {
            // Grouper les ventes par ref_vente
            const grouped = {};
            data.venteDetails.forEach(vente => {
              const key = vente.refVente;
              if (grouped[key]) {
                grouped[key].qty += vente.qty;
              } else {
                grouped[key] = { ...vente };
              }
            });
            
            Object.values(grouped).forEach(vente => {
              rows.push([
                account.source,
                account.email,
                account.password || '',
                match.matchNum,
                cat,
                data.qty,
                data.attribue,
                data.qty - data.attribue,
                vente.refVente,
                vente.qty,
                vente.nomClient,
                vente.mailClient
              ]);
            });
          } else {
            rows.push([
              account.source,
              account.email,
              account.password || '',
              match.matchNum,
              cat,
              data.qty,
              data.attribue,
              data.qty - data.attribue,
              '-',
              '-',
              '-',
              '-'
            ]);
          }
        });
      });
    });

    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recap_comptes_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé !');
  };

  return (
    <div className="space-y-4">
      {/* Header avec stats globales */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5 text-[#C4A052]" />
            <span className="text-white font-bold">{globalStats.totalSources} sources</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-[#C4A052]" />
            <span className="text-white font-bold">{globalStats.total} comptes</span>
          </div>
          <div className="flex items-center gap-2 text-green-400">
            <Check className="h-4 w-4" />
            <span className="font-medium">{globalStats.ready} prêts</span>
          </div>
          <div className="flex items-center gap-2 text-orange-400">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium">{globalStats.partial} partiels</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Toggle Vue */}
          <div className="flex bg-zinc-800 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('source')}
              className={cn(
                "px-3 py-1 text-xs rounded-md transition-colors",
                viewMode === 'source' ? "bg-[#C4A052] text-black font-bold" : "text-zinc-400 hover:text-white"
              )}
            >
              Par Source
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={cn(
                "px-3 py-1 text-xs rounded-md transition-colors",
                viewMode === 'flat' ? "bg-[#C4A052] text-black font-bold" : "text-zinc-400 hover:text-white"
              )}
            >
              Liste
            </button>
          </div>

          {/* Filtre prêts seulement */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyReady}
              onChange={(e) => setShowOnlyReady(e.target.checked)}
              className="rounded border-zinc-600 bg-zinc-800 text-[#C4A052] focus:ring-[#C4A052]"
            />
            <span className="text-sm text-zinc-400">Prêts seulement</span>
          </label>

          {/* Tri */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded px-3 py-1 text-sm text-white"
          >
            <option value="reste">Trier par reste</option>
            <option value="total">Trier par total</option>
            <option value="email">Trier par email</option>
          </select>

          {/* Bouton Export */}
          <Button
            onClick={handleExportRecap}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Vue par SOURCE */}
      {viewMode === 'source' && (
        <div className="space-y-4">
          {sourceStats.map(sourceData => {
            const isSourceExpanded = expandedSources.has(sourceData.source);
            const sourceIsReady = sourceData.totalReste === 0 && sourceData.totalQty > 0;
            
            // Filtrer les comptes si showOnlyReady
            const filteredAccounts = showOnlyReady 
              ? sourceData.accountList.filter(a => a.totalReste === 0 && a.totalQty > 0)
              : sourceData.accountList;
            
            if (showOnlyReady && filteredAccounts.length === 0) return null;

            return (
              <div key={sourceData.source} className="border border-zinc-700 rounded-lg overflow-hidden bg-zinc-900/30">
                {/* Header de la SOURCE - cliquable */}
                <div
                  onClick={() => toggleSourceExpand(sourceData.source)}
                  className={cn(
                    "p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between",
                    isSourceExpanded ? "bg-zinc-800/30" : ""
                  )}
                >
                  <div className="flex items-center gap-4">
                    {isSourceExpanded ? (
                      <FolderOpen className="h-6 w-6 text-[#C4A052]" />
                    ) : (
                      <Folder className="h-6 w-6 text-[#C4A052]" />
                    )}
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-bold text-lg">{sourceData.source}</span>
                        <span className="bg-zinc-700 text-zinc-300 text-xs px-2 py-0.5 rounded">
                          {sourceData.accountCount} compte{sourceData.accountCount > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-zinc-400">{sourceData.totalQty} billets</span>
                        <span className="text-green-400">{sourceData.totalAttribue} attribués</span>
                        {sourceData.totalReste > 0 && (
                          <span className="text-orange-400">RESTE {sourceData.totalReste}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Badge statut */}
                    <div className={cn(
                      "px-3 py-1 rounded-lg text-sm font-bold",
                      sourceIsReady ? "bg-green-500/20 text-green-400" : "bg-orange-500/20 text-orange-400"
                    )}>
                      {sourceIsReady ? `✓ ${sourceData.readyCount} PRÊTS` : `${sourceData.readyCount}/${sourceData.accountCount} prêts`}
                    </div>
                    
                    <ChevronDown className={cn(
                      "h-5 w-5 text-zinc-400 transition-transform",
                      isSourceExpanded ? "rotate-180" : ""
                    )} />
                  </div>
                </div>

                {/* Liste des comptes de cette source */}
                {isSourceExpanded && (
                  <div className="p-4 border-t border-zinc-700 bg-zinc-950/30">
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                      {filteredAccounts.map(account => renderAccount(account, true))}
                    </div>
                    {filteredAccounts.length === 0 && (
                      <div className="text-center py-4 text-zinc-500 text-sm">
                        Aucun compte prêt dans cette source
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Vue FLAT (liste simple) */}
      {viewMode === 'flat' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {flatAccountStats.map(account => renderAccount(account, false))}
        </div>
      )}

      {/* Message vide */}
      {((viewMode === 'source' && sourceStats.length === 0) || 
        (viewMode === 'flat' && flatAccountStats.length === 0)) && (
        <div className="text-center py-12 text-zinc-500">
          {showOnlyReady ? "Aucun compte prêt à livrer" : "Aucun compte trouvé"}
        </div>
      )}

      {/* Modal de confirmation de validation */}
      <Dialog open={!!validationModal} onOpenChange={() => setValidationModal(null)}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-green-400">
              <CheckCircle className="h-6 w-6" />
              Confirmer la validation
            </DialogTitle>
          </DialogHeader>

          {validationModal && (
            <div className="space-y-4">
              <p className="text-zinc-300">
                Voulez-vous passer cette vente en <strong className="text-green-400">VALIDÉ</strong> ?
              </p>

              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Réf. Vente:</span>
                  <span className="text-amber-400 font-mono font-bold">{validationModal.refVente}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Client:</span>
                  <span className="text-white">{validationModal.nomClient || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Email client:</span>
                  <span className="text-zinc-300 text-xs truncate">{validationModal.mailClient || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Quantité:</span>
                  <span className="text-white font-bold">x{validationModal.qty}</span>
                </div>
                <hr className="border-zinc-700" />
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Match:</span>
                  <span className="text-[#C4A052] font-bold">M{validationModal.matchNum}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Catégorie:</span>
                  <span className="text-white">{validationModal.category}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">Compte ACHAT:</span>
                  <span className="text-zinc-300 text-xs truncate">{validationModal.accountEmail}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-500">PW Compte:</span>
                  <span className="text-zinc-400 font-mono text-xs">{validationModal.accountPassword || '****'}</span>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-300 text-sm">
                  ✓ La VENTE et les ACHAT liés seront passés en statut <strong>VALIDE</strong>
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setValidationModal(null)}
              disabled={isValidating}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            >
              Annuler
            </Button>
            <Button
              onClick={handleValidateVente}
              disabled={isValidating}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validation...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Valider
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsView;
